import React, { useCallback } from "react";
import * as iconModule from "react-icons/md";
import { X } from "lucide-react";
import { ActionIcon, Collapse } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { getDynamicIcon, replacePlaceHolders, snackbarColors } from "./utils";
import { useSnackbars } from "./SnackbarsProvider";
import { Markdown } from "../../utils/Markdown";
import { ISnackbar } from "./types";
import { AnalyticsContext, DocumentEventInterface } from "../AnalyticsProvider";

interface ISnackbarUIProps {
  snackbar: ISnackbar;
  pos: number;
  count: number;
}

const Snackbar: React.FC<ISnackbarUIProps> = ({ snackbar, pos, count }) => {
  const snackbarRef = React.useRef(null);
  const { removeSnackbar } = useSnackbars();
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  const navigate = useNavigate();
  const [isExiting, setIsExiting] = React.useState(false);

  const handleClose = useCallback(
    (redirect: boolean) => {
      setIsExiting(true);

      let trackingData: DocumentEventInterface | undefined;

      if (redirect) {
        if (snackbar.id.toString().startsWith("release-")) {
          trackingData = {
            event: "Batch_Ready_Snackbar_CTA",
            description:
              'User clicks on the "Take me there button" on this snackbar.',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        } else if (snackbar.id.toString().startsWith("release_batch-")) {
          trackingData = {
            event: "Batch_Unsent_Reminder_Snackbar_CTA",
            description:
              'User clicks on the "Take me there button" on this snackbar. ',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        } else if (snackbar.id.toString().startsWith("fix-")) {
          trackingData = {
            event: "Batch_Ready_Unassigned_Snackbar_CTA",
            description:
              'User clicks on the "Take me there button" on this snackbar.',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        }
      } else if (!redirect) {
        if (snackbar.id.toString().startsWith("release-")) {
          trackingData = {
            event: "Batch_Ready_Snackbar_Close",
            description:
              'User clicks on the "Take me there button" on this snackbar.',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        } else if (snackbar.id.toString().startsWith("release_batch-")) {
          trackingData = {
            event: "Batch_Unsent_Reminder_Snackbar_Close",
            description:
              'User clicks on the "X" close button on this snackbar.',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        } else if (snackbar.id.toString().startsWith("fix-")) {
          trackingData = {
            event: "Batch_Unsent_Reminder_Snackbar_Close",
            description:
              'User clicks on the "X" close button on this snackbar. ',
            event_type: "CTA:Click",
            job: snackbar.data,
            type: snackbar.template.attributes.resource_type,
          };
        }
      }

      setTimeout(() => {
        removeSnackbar(snackbar.id);
        if (trackingData) {
          trackDocumentsEvent({
            ...trackingData,
          });
        }
        if (redirect) {
          navigate(
            `/${snackbar.template.attributes.resource_type.toLowerCase()}?action=${
              snackbar.id
            }`
          );
        }
      }, 300);
    },
    [snackbar]
  );

  if (pos > 2) {
    return null;
  }

  const message = replacePlaceHolders(
    snackbar.template.attributes.message_content,
    snackbar.data
  );
  const colors = snackbarColors(snackbar.type);

  return (
    <div
      ref={snackbarRef}
      className="flex w-full gap-3 p-4 rounded-md shadow-sm align-start"
      style={{
        ...(pos > 0
          ? {
              position: "absolute",
              bottom: `${(count - pos - 1) * 5}px`,
              transform: `scale(${1 - 0.01 * pos})`,
              transformOrigin: "bottom center",
            }
          : {
              position: "relative",
            }),
        zIndex: count - pos,
        backgroundColor: colors.backgroundColor,
        border: `1px solid ${colors.borderColor}`,
        animation: isExiting
          ? "snackbarOutro 300ms forwards"
          : "snackbarIntro 300ms forwards",
      }}
    >
      {snackbar.template?.attributes?.icon && (
        <div className="w-[24px] h-[24px]">
          {getDynamicIcon(snackbar.template?.attributes?.icon, iconModule, 24)}
        </div>
      )}
      <Markdown text={message} style={{ flexGrow: 1 }} />
      {snackbar.template?.attributes?.action_text && (
        <button
          className="btn btn-primary whitespace-nowrap"
          style={{
            backgroundColor: colors.btnColor,
            color: "#fff",
            border: "none",
            padding: "0.5rem 1rem",
            borderRadius: "0.375rem",
            height: "fit-content",
          }}
          onClick={() => handleClose(true)}
        >
          {snackbar.template?.attributes?.action_text}
        </button>
      )}
      <ActionIcon
        onClick={() => handleClose(false)}
        title="Close"
        aria-label="Close"
      >
        <X size={18} />
      </ActionIcon>
    </div>
  );
};

const Snackbars: React.FC = () => {
  const { snackbars } = useSnackbars();
  return (
    <Collapse in={snackbars.length > 0}>
      <div className="p-4">
        <div
          className="relative w-full max-w-screen-lg m-auto overflow-hidden rounded"
          style={{
            paddingBottom: `${snackbars.slice(1, 3).length * 5}px`,
          }}
        >
          {snackbars.slice(0, 3).map((snackbar, index) => (
            <Snackbar
              key={snackbar.id}
              snackbar={snackbar}
              pos={index}
              count={snackbars.slice(0, 3).length}
            />
          ))}
        </div>
      </div>
    </Collapse>
  );
};

export default Snackbars;
